/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import { legacyConvertCurrency } from 'utils/general';
import clsx from 'clsx';
import { css } from '@emotion/react';
import { EVENT_COLORS } from 'utils/constants';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

import { usePrefs } from 'hooks/usePrefs';
import { useAction } from 'hooks/useAction';
import Clickwrap from 'components/Clickwrap';
import { Spinner } from 'components/Loading';
import {
  updateEventStore as updateEventStoreAction,
  updateTaskStore as updateTaskStoreAction,
} from 'containers/Events/actions';
import { getMembers } from 'api/account';
import {
  updateWeddingChecklistItem,
  updateGeneralChecklistItem,
  deleteCalendarEvent,
} from 'api/events';
import {
  updateChecklistItem as updateLeadChecklistItem,
} from 'api/leads';
import APModal from 'components/Modal/APModal';
import CalendarSyncSettings from 'pages/Calendar/components/settings/CalendarSyncSettings';
import BookedLeadEventCard from 'components/Calendar/components/BookedLeadEventCard';
import BookedProjectEventCard from 'components/Calendar/components/BookedProjectEventCard';
import TentativeBookingEventCard from 'components/Calendar/components/TentativeBookingEventCard';
import PaymentEventCard from 'components/Calendar/components/PaymentEventCard';
import { getDayjsTimeFormat } from 'utils/time';
import { getDayjsDateFormat } from 'utils/date';
import useConnectModal from 'components/ConnectModal';
import CardContainer from './components/CardContainer';
import EventCard from './components/EventCard';
import TaskCard from './components/TaskCard';
import ExternalEventCard from './components/ExternalEventCard';
import CalendarSidePanel from './Calendar.SidePanel';
import CalendarViewOptions from './Calendar.ViewOptions';
import CalendarTimeZoneSelect from './Calendar.TimeZoneSelect';
import DateNavigationForm from './Calendar.DateNavigationForm';


dayjs.extend(timezone);
dayjs.extend(localeData);

const Calendar = (props) => {
  const {
    projects,
    selectedDate,
    timeZone,
    events,
    eventsLoading,
    onDateChange,
    refetchEvents,
    integration,
    loginCalendarsById,
    displayDateTip = false,
    setDisplayDateTip = () => {},
    ...restProps
  } = props;

  const { calendar: calendarViewOptions, updateCalendarPrefs, updatePrefs } = usePrefs();
  const userInfo = useSelector((state) => state.global.userInfo);
  const effectivePermissions = useSelector((state) => state.projects.effectivePermissions || {});
  const { id: orgId, timePreference } = userInfo?.organization || {};
  const {  integrations = [], organization } = userInfo || {};
  const validIntegrations = integrations.filter((i) => i.grantStatus === 'valid');

  const userDetails = {
    timeZone: userInfo.timezone,
    datePreference: organization.datePreference,
    timePreference: organization.timePreference,
    dateFormat: getDayjsDateFormat(organization.datePreference),
    timeFormat: getDayjsTimeFormat(organization.timePreference),
    loginCalendarsById,
    integration,
  };

  const updateEventStore = useAction(updateEventStoreAction);
  const updateTaskStore = useAction(updateTaskStoreAction);

  const {
    firstDayOfWeek,
    showChecklist,
    showEvents,
    showPayments,
    showBookings,
    showProjects,
    weddingExclusions,
    externalCalendarsDisplay,
    microfilters: calendarMicrofilters,
  } = calendarViewOptions || {};

  const [reference, setReference] = useState();
  const [eventDetails, setEventDetails] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [sidepanelExpanded, setSidepanelExpanded] = useState(true);
  const [calendarApi, setCalendarApi] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarView, setCalendarView] = useState(null);
  const [isCalendarSetupComplete, setCalendarSetupComplete] = useState(false);
  const [manageCalendarOpen, setManageCalendarOpen] = useState(false);
  const [orgMembers, setOrgMembers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(null);

  // Updates to the calendar component, such as going to the next month, does not
  // cause react to re-render. So in order to have a custom header, we have to
  // add the title into state and update this from a callback function.
  const [currentMonth, setCurrentMonth] = useState(dayjs().format('MMMM YYYY'));

  const calendarRef = useRef(null);

  const editableProjects = useMemo(() => {
    return projects.filter((project) => {
      const permissions = effectivePermissions?.[project.id];
      return permissions?.['calendar.edit'] === true;
    });
  }, [projects, effectivePermissions]);

  const {
    setConnectedModalOpen,
    setConnectModalOpen,
    setNotEnabledOpen,
    setRedirectPath,
    deleteRedirectPath,
    ConnectModal,
  } = useConnectModal();

  const sortedProjects = projects.slice().sort((a, b) => String(a.name).localeCompare(String(b.name)));

  const { events: eventsMF, tasks: tasksMF, payments: paymentsMF } = calendarMicrofilters;
  const { general: showGeneralEvents = true, project: showProjectEvents = true } = eventsMF || {};
  const {
    general: showGeneralTasks = true,
    sales: showSalesTasks = true,
    project: showProjectTasks = true,
    assigned: showAssignedTasks = false, // If true, filters to only tasks assigned to current user.
    completed: showCompletedTasks = true,
  } = tasksMF || {};
  const {
    invoice: showInvoicePayments = true,
    vendor: showVendorPayments = true,
    completed: showCompletedPayments = true,
  } = paymentsMF || {};

  const isDragAndDropEnabled = calendarView === 'dayGridMonth' && !eventsLoading;

  function handleCalendarPageClick(e) {
    const { target } = e;
    // If click is on the page-title, toggle the date navigation modal.
    const isPageTitle = target.closest('.calendar-title') !== null;
    // False if not a child of .calendar-date-navigation-modal
    const isDateNavigation = target.closest('.calendar-date-navigation-modal') !== null;
    const isEventclick = target.closest('.fc-event') !== null;
    const isDateClick = target.closest('.fc-daygrid-day') !== null;
    if (!isDateNavigation && !isPageTitle && !isEventclick && !isDateClick && displayDateTip) {
      setDisplayDateTip(false);
    }
  }

  const eventClick = (eventClickInfo) => {
    if (isOpen) {
      closeEventPopup();
      return;
    }

    if (displayDateTip) {
      setDisplayDateTip(false);
      return;
    }

    const { jsEvent, event } = eventClickInfo;
    const { target } = jsEvent || {};
    if (target) {
      setReference(target);
      setIsOpen(true);
    }
    if (event) {
      setEventDetails(event);
    }
  };

  const dateClick = (e) => {
    if (isOpen) {
      closeEventPopup();
      return;
    }

    if (displayDateTip) {
      setDisplayDateTip(false);
      return;
    }

    const { jsEvent: { target } = {} } = e;
    setSelectedDay(e.dateStr);

    if (target) {
      setReference(target);
      setIsOpen(true);
    }
  };

  const closeEventPopup = () => {
    setHasUnsavedChanges(null);
    setIsOpen(false);
    setEventDetails(null);
    setReference(null);
  };

  const calcCalendarEventVisibility = (event) => {
    if (!showEvents) return false;
    const { weddingId } = event.extendedProps || event;
    if (!showProjectEvents && weddingId) return false;
    if (!showGeneralEvents && !weddingId) return false;

    if (weddingId && effectivePermissions?.[weddingId]?.['calendar.edit'] !== true) {
      return false;
    }

    return true;
  };

  const calcTasksVisibility = (task) => {
    if (!showChecklist) return false;

    const { weddingId, leadId, completed, resourceIds = [] } = task.extendedProps || task;

    // Don't show assigned tasks if view option is unselected.
    if (showAssignedTasks && !resourceIds.includes(userInfo?.id)) return false;

    // Don't show completed tasks if view option is unselected.
    if (!showCompletedTasks && completed) return false;

    // Don't show specific tasks.
    const type = !weddingId && !leadId ? 'general' : weddingId ? 'project' : 'sales';
    if (!showGeneralTasks && type === 'general') return false;
    if (!showProjectTasks && type === 'project') return false;
    if (!showSalesTasks && type === 'sales') return false;

    return true;
  };

  const calcPaymentsVisibility = (payment) => {
    if (!showPayments) return false;

    const { weddingId, paidOn } = payment.extendedProps || payment;
    if (!showInvoicePayments && !weddingId) return false;
    if (!showVendorPayments && weddingId) return false;
    if (!showCompletedPayments && paidOn) return false;

    return true;
  };

  const calcEventVisibility = (event) => {
    const { type, weddingId, loginCalendarId } = event.extendedProps || event;

    const isVisible = () => {
      if (Array.isArray(weddingExclusions) && weddingExclusions.includes(String(weddingId))) {
        return false;
      }
      if (loginCalendarId && (
        !Object.prototype.hasOwnProperty.call(externalCalendarsDisplay, loginCalendarId) ||
        externalCalendarsDisplay?.[loginCalendarId] === false)
      ) {
        return false;
      }

      switch (type) {
        case 'checklist':
          return calcTasksVisibility(event);
        case 'wedding':
          return calcCalendarEventVisibility(event);
        case 'external':
          return !!externalCalendarsDisplay?.[event.loginCalendarId];
        case 'payment_due':
          return calcPaymentsVisibility(event);
        case 'booking':
          return showBookings;
        case 'project_date':
        case 'booked_lead':
          return showProjects;
        default:
          return false;
      }
    };

    return isVisible() ? 'auto' : 'none';
  };

  const filteredEvents = useMemo(() => {
    return events.map(ev => ({ ...ev, display: calcEventVisibility(ev) }));
  },
  [
    events,
    showChecklist,
    showEvents,
    showPayments,
    showBookings,
    showProjects,
    weddingExclusions,
    externalCalendarsDisplay,
    calendarMicrofilters,
    effectivePermissions,
  ]);

  const nextPrevDate = (direction) => {
    if (calendarApi) {
      if (direction === 'next') {
        calendarApi.next();
      } else {
        calendarApi.prev();
      }
    }
  };

  const toggleDateTip = () => {
    setDisplayDateTip(prev => !prev);
  };

  const toggleSidePanel = () => {
    setSidepanelExpanded(prev => !prev);
  };

  useEffect(() => {
    if (calendarApi) {
      calendarApi.gotoDate(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (calendarRef) {
      setCalendarApi(calendarRef.current.getApi());
    }
  }, [calendarRef]);

  useEffect(() => {
    if (calendarApi) {
      const view = calendarApi.view.type;
      setCalendarView(view);
      setCalendarSetupComplete(true);
    }
  }, [calendarApi]);

  const handleEventTypeToggle = (e) => {
    const { id, checked } = e.target;
    updateCalendarPrefs(id, checked);
  };

  const handleExternalCalendarToggle = (e) => {
    const { id, checked } = e.target;

    updateCalendarPrefs(
      'externalCalendarsDisplay',
      {
        ...calendarViewOptions.externalCalendarsDisplay,
        [id]: checked,
      },
    );
  };


  const handleProjectExclusionToggle = (e) => {
    const { id, checked } = e.target;
    const stringId = String(id);
    let updatedExclusions = [];
    if (checked) {
      updatedExclusions = weddingExclusions.slice().filter((item) => item !== stringId);
    } else {
      updatedExclusions = weddingExclusions.includes(stringId) ?
        [...weddingExclusions] : [...weddingExclusions, stringId];
    }
    // Remove duplicates.
    updatedExclusions = [...new Set(updatedExclusions)];
    updateCalendarPrefs('weddingExclusions', updatedExclusions);
  };

  const handleAllProjectExclusions = (value) => {
    if (value) {
      updateCalendarPrefs('weddingExclusions', sortedProjects.map(project => String(project.id)));
    } else {
      updateCalendarPrefs('weddingExclusions', []);
    }
  };

  const handleFirstDayChange = (value) => {
    updateCalendarPrefs('firstDayOfWeek', value);
  };

  const handleCalendarSyncSettingsToggle = async (loginCalendarId, isSynced) => {
    // Remove or add the calendar from the user preferences.
    let newExternalCalendarsDisplay;
    if (isSynced) {
      newExternalCalendarsDisplay = { ...externalCalendarsDisplay, [loginCalendarId]: true };
      // Only need to refetch events if adding a new external calendar.
      refetchEvents(currentMonth);
    } else {
      // Removing a calendar means we only need to filter out events from that external calendar.
      newExternalCalendarsDisplay = { ...externalCalendarsDisplay };
      delete newExternalCalendarsDisplay[loginCalendarId];
    }

    updateCalendarPrefs('externalCalendarsDisplay', newExternalCalendarsDisplay);
  };

  const handleManageCalendar = () => {
    // Open the calendar management modal
    setManageCalendarOpen(!manageCalendarOpen);
  };

  const handleConnectCalendar = () => {
    // Check if AP Connect enabled
    // If not display message modal
    const { integratedStatus } = userInfo.personas || {};
    if (integratedStatus === 'disabled') {
      setNotEnabledOpen(true);
    } else {
      // If enabled, open the integrations connection modal
      setConnectModalOpen(true);
      setRedirectPath(`${document.location.pathname}?manage-calendar=true`);
    }
  };

  const updateChecklistItem = async (event, updatedFields) => {
    const {
      weddingId,
      leadId,
      taskId,
      details,
      completed,
      dueDate,
      timeframe,
      assignmentNote,
      isPrivate,
      clientVisible,
    } = event.extendedProps || {};

    const updatedData = {
      name: event.title,
      details,
      completed,
      due_date: dueDate,
      timeframe,
      assignment_note: assignmentNote,
      ...updatedFields,
    };

    let responseUpdate;

    try {
      if (weddingId) {
        updatedData.is_private = isPrivate;
        updatedData.client_visible = clientVisible;

        responseUpdate = await updateWeddingChecklistItem({
          weddingId,
          taskId,
          itemData: updatedData,
        });
      } else if (leadId) {
        responseUpdate = await updateLeadChecklistItem(
          orgId,
          leadId,
          taskId,
          updatedData,
        );
      } else {
        responseUpdate = await updateGeneralChecklistItem({
          taskId,
          itemData: updatedData,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Checklist update error:', error);
    }
    return responseUpdate;
  };

  const handleEventCheckedChange = async (event, explicitCompletedState = null) => {
    const currentCompleted = explicitCompletedState !== null
      ? explicitCompletedState
      : event?.extendedProps?.completed ?? false;

    const updatedFields = { completed: !currentCompleted };
    const responseUpdate = await updateChecklistItem(event, updatedFields);

    if (responseUpdate?.ok) {
      const { taskId, weddingId, leadId } = event.extendedProps;
      const taskType = weddingId ? 'weddings' : leadId ? 'leads' : 'loginTasks';
      updateEventStore({ id: taskId, data: { completed: updatedFields.completed } });
      updateTaskStore({ id: taskId, taskType, data: { completed: updatedFields.completed } });
    }
  };

  const handleEventDrop = async (info) => {
    const { event, oldEvent } = info;
    const updatedFields = { due_date: dayjs.utc(event.start).format('YYYY-MM-DD') };

    const { startStr: originalStart, endStr: originalEnd } = oldEvent;
    const { dueDate: originalDueDate } = oldEvent.extendedProps;

    const responseUpdate = await updateChecklistItem(event, updatedFields);

    const { due_date } = updatedFields;
    const { taskId, weddingId, leadId } = event.extendedProps;
    const taskType = weddingId ? 'weddings' : leadId ? 'leads' : 'loginTasks';
    updateEventStore({ id: taskId, data: { dueDate: due_date, start: due_date, end: due_date } });
    updateTaskStore({ id: taskId, taskType, data: { dueDate: due_date } });

    if (!responseUpdate?.ok) {
      // eslint-disable-next-line no-console
      console.error('Drop event update error');
      // Rollback the optimistic updates above
      updateEventStore({ id: taskId, data: { dueDate: originalDueDate, start: originalStart, end: originalEnd } });
      updateTaskStore({ id: taskId, taskType, data: { dueDate: originalDueDate } });
      info.revert();
    }
  };

  const getDayPopoverFormat = () => ({
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const handleMicrofilterChange = (type, kind, value) => {
    if (
      Object.prototype.hasOwnProperty.call(calendarMicrofilters, type) &&
      Object.prototype.hasOwnProperty.call(calendarMicrofilters[type], kind)
    ) {
      updateCalendarPrefs(
        'microfilters',
        {
          ...calendarMicrofilters,
          [type]: {
            ...calendarMicrofilters[type],
            [kind]: value,
          },
        },
      );
    }
  };

  const handleFilterOverrides = (projectId) => {
    const stringProjectId = String(projectId);
    const prefsToUpdate = {};

    if (!showEvents) {
      prefsToUpdate['calendar.showEvents'] = true;
    }

    if (weddingExclusions.includes(stringProjectId)) {
      const updatedExclusions = weddingExclusions.filter(id => id !== stringProjectId);
      prefsToUpdate['calendar.weddingExclusions'] = updatedExclusions;
    }

    if (Object.keys(prefsToUpdate).length > 0) {
      updatePrefs(prefsToUpdate);
    }
  };

  const renderEventContent = ({ event }) => {
    const { extendedProps } = event;

    if (extendedProps?.type === 'checklist') {
      return (
        <div className={clsx('event-card__checkbox', extendedProps.completed && 'checked' )}>
          <Clickwrap
            name={`check-event-${event.id}`}
            checked={extendedProps.completed}
            className={clsx(
              event?.classNames,
              'clickwrap--view-option',
              'small',
            )}
            onChange={() => handleEventCheckedChange(event)}
            color={EVENT_COLORS[extendedProps.type]}
            colorFill
          />
          <span className="event-card__checkbox-text">{event.title}</span>
        </div>
      );
    }

    if (extendedProps?.type === 'payment_due') {
      const isPastDue = dayjs().isAfter(dayjs(event.start), 'day');
      const formattedAmount = extendedProps?.amount != null
        ? legacyConvertCurrency(extendedProps.amount, extendedProps.weddingCurrency)
        : null;

      return (
        <div
          css={css`
            &::before {
              background: ${
                EVENT_COLORS[extendedProps.type] ?
                  `${EVENT_COLORS[extendedProps.type]} !important` : 'inherit'
              };
            }
          `}
          className={clsx(
            event?.classNames,
            'payment',
            extendedProps.paidOn && 'paid',
            !extendedProps.paidOn && isPastDue && 'past-due',
          )}
        >
          <div className="payment__title">
            {extendedProps.paymentType === 'invoice' ? 'Payment Due ' : 'Vendor Payment Due '}
            {formattedAmount && `(${formattedAmount})`}
          </div>
        </div>
      );
    }

    return true;
  };

  const renderCardContent = () => {
    const { weddingId, type } = eventDetails?.extendedProps || {};
    const wedding = weddingId ? projects.find(project => project.id === weddingId) : null;

    switch (type) {
      case 'checklist':
        return (
          <TaskCard
            eventDetails={eventDetails}
            orgMembers={orgMembers}
            onToggleCompleted={handleEventCheckedChange}
            project={wedding}
            timeZone={timeZone}
            userId={userInfo.id}
            userDetails={userDetails}
          />
        );
      case 'project_date':
        return (
          <BookedProjectEventCard
            eventDetails={eventDetails}
            userDetails={userDetails}
          />
        );
      case 'booking':
        return (
          <TentativeBookingEventCard
            eventDetails={eventDetails}
            userDetails={userDetails}
          />
        );
      case 'booked_lead':
        return (
          <BookedLeadEventCard
            eventDetails={eventDetails}
            userDetails={userDetails}
          />
        );
      case 'payment_due':
        return (
          <PaymentEventCard
            eventDetails={eventDetails}
            project={wedding}
            userDetails={userDetails}
          />
        );
      case 'external':
        return (
          <ExternalEventCard
            eventDetails={eventDetails}
            userDetails={userDetails}
          />
        );
      default:
        return (
          <EventCard
            key={`event-card--${eventDetails?.id}`}
            reference={reference}
            setReference={setReference}
            eventDetails={eventDetails}
            setEventDetails={setEventDetails}
            open={isOpen}
            setOpen={setIsOpen}
            onClose={closeEventPopup}
            projects={editableProjects}
            myTimeZone={timeZone}
            selectedDay={selectedDay}
            calendarRef={calendarRef}
            userDetails={userDetails}
            onUnsavedChanges={setHasUnsavedChanges}
            handleCalendarEventDelete={() => handleCalendarEventDelete(eventDetails)}
            handleFilterOverrides={handleFilterOverrides}
          />
        );
    }
  };

  const fetchOrgMembers = async () => {
    const response = await getMembers(orgId);

    if (response.ok && response.data) {
      setOrgMembers(response.data);
    }
  };

  useEffect(() => {
    fetchOrgMembers();

    // Get url query params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('manage-calendar') === 'true') {
      setConnectedModalOpen(
        true,
        () => setManageCalendarOpen(true),
      );
      // Remove the query param from the URL
      const newUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, document.title, newUrl);
      // Delete the cookie
      deleteRedirectPath();
    }
  }, []);

  // MutationObserver to constrain fc-more-popover within calendar boundaries
  useEffect(() => {
    function constrainPopoverToCalendar(popover) {
      const popoverRect = popover.getBoundingClientRect();
      const calendarWrapper = document.getElementById('calendar-wrapper');
      const calendarRect = calendarWrapper?.getBoundingClientRect();

      if (!calendarRect) return;

      // Parse top value from style
      let adjustedTop = parseFloat(popover.style.top) || 0;

      // Check if popover extends beyond calendar's bottom edge
      if (popoverRect.bottom > calendarRect.bottom) {
        adjustedTop -= popoverRect.bottom - calendarRect.bottom;
        popover.style.top = `${adjustedTop}px`;
      }
    }

    // Adding eslint disables since web APIs register as undefined
    /* eslint-disable-next-line no-undef */
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is the popover or contains it
          /* eslint-disable-next-line no-undef */
          if (node.nodeType === Node.ELEMENT_NODE) {
            const popover = node.classList?.contains('fc-more-popover')
              ? node
              : node.querySelector?.('.fc-more-popover');

            if (popover) {
              // FullCalendar applies positioning immediately, so use a microtask
              // to ensure the layout is complete before constraining
              setTimeout(() => {
                constrainPopoverToCalendar(popover);
              }, 0);
            }
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup on unmount
    return () => observer.disconnect();
  }, []);

  const handleDateChange = (info) => {
    if (!isCalendarSetupComplete) return;

    if (!info.view) return;

    const { viewTitle } = info.view.getCurrentData();
    setCurrentMonth(viewTitle);
    onDateChange(info);
  };

  const getEventClassNames = (arg) => {
    const { event } = arg;
    const { extendedProps } = event || {};
    const { type } = extendedProps || {};

    const now = dayjs().tz(timeZone);
    const start = dayjs(event.startStr).tz(timeZone);

    const classNames = [];

    if (type) {
      switch (type) {
        case 'wedding':
          classNames.push('wedding');
          // Falls through.
        case 'project_date':
          classNames.push('internal');
          break;
        case 'checklist':
          classNames.push('checklist');
          break;
        case 'payment_due':
          classNames.push('payment-due');
          break;
        default:
          break;
      }
    }

    if (start.isSame(now, 'day') && start.isBefore(now)) {
      classNames.push('today-start-past');
    }

    return classNames;
  };

  const buildCalendarOrder = (integrationsList) => {
    return integrationsList.flatMap((_integration) =>
      [..._integration.loginCalendars]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((calendar) => calendar.calendarsId),
    );
  };

  const externalCalendarsOrder = useMemo(
    () => buildCalendarOrder(validIntegrations),
    [validIntegrations],
  );

  const eventOrder = (calendarOrder) => {
    const typeSortPriority = ['project_date', 'booked_lead', 'wedding', 'external', 'booking'];

    const getOrder = (event) => {
      const { type, externalCalendarId } = event.extendedProps || {};
      const isExternal = type === 'external';
      const isTask = type === 'checklist';
      const isPayment = type === 'payment_due';
      const isAllDay = event.allDay && typeSortPriority.includes(type);

      // 1. High-level group order: All-day (0), Task (1), Payment (2), Timed (3)
      const eventGroupOrder = isAllDay ? 0 : isTask ? 1 : isPayment ? 2 : 3;

      // 2. Fine-grained type order based on configured priority
      const typeOrder = typeSortPriority.includes(type)
        ? typeSortPriority.indexOf(type)
        : Number.MAX_SAFE_INTEGER;

      // 3. External calendar order
      const calendarOrd = calendarOrder.includes(externalCalendarId)
        ? calendarOrder.indexOf(externalCalendarId)
        : Number.MAX_SAFE_INTEGER;

      return {
        eventGroupOrder,
        isAllDay,
        isExternal,
        typeOrder,
        calendarOrd,
        time: new Date(event.start).getTime(),
      };
    };

    return (a, b) => {
      const orderA = getOrder(a);
      const orderB = getOrder(b);

      // 1. Compare by high-level event group
      if (orderA.eventGroupOrder !== orderB.eventGroupOrder) {
        return orderA.eventGroupOrder - orderB.eventGroupOrder;
      }

      // 2. If both are all-day events, sort by type order, then calendar order if both external
      if (orderA.isAllDay && orderB.isAllDay) {
        if (orderA.typeOrder !== orderB.typeOrder) return orderA.typeOrder - orderB.typeOrder;
        if (orderA.isExternal && orderB.isExternal) return orderA.calendarOrd - orderB.calendarOrd;
      }

      // 3. If events have different start times, sort chronologically
      if (orderA.time !== orderB.time) return orderA.time - orderB.time;

      // 4. If same start time, sort by type order
      if (orderA.typeOrder !== orderB.typeOrder) return orderA.typeOrder - orderB.typeOrder;

      // 5. If both external, use calendar order
      if (orderA.isExternal && orderB.isExternal) return orderA.calendarOrd - orderB.calendarOrd;

      // 6. Events are equal in all sorting aspects
      return 0;
    };
  };

  const getEventTimeFormat = (pref = '12') => ({
    hour: 'numeric',
    minute: '2-digit',
    hour12: pref === '12',
    meridiem: 'short',
    omitZeroMinute: pref === '12',
  });

  const handleEventMount = ({ event, el }) => {
    // event.textColor isn’t applied to timed events (dot view),
    // so set --event-text-color manually.
    if (event?.textColor) {
      el.style.setProperty('--event-text-color', event.textColor);
    }

    // Need to adjust the popup reference if it shifts because of a react state change.
    if (eventDetails?.id === event.id) {
      setReference(el);
    }
  };

  const eventType = eventDetails?.extendedProps?.type;
  const hasImage = Boolean(eventDetails?.extendedProps?.image306);
  // const showDeleteButton = eventType === 'wedding' && eventDetails?.extendedProps?.weddingId;
  const cardContainerClasses = clsx(
    eventType === 'project_date' && 'card-container__info-card booked-project-info-card',
    eventType === 'payment_due' && 'card-container__info-card payment-info-card',
    !hasImage && 'no-image',
  );

  const handleCalendarEventDelete = async (event) => {
    const { extendedProps, id } = event;
    const { weddingId } = extendedProps || {};

    if (!weddingId) {
      return;
    }

    try {
      const response = await deleteCalendarEvent({
        eventId: eventDetails.id,
        weddingId,
      });

      if (response.ok) {
        // To avoid losing reference and dealing with
        // floatingStyles positioning, best to just close this now
        closeEventPopup();
        updateEventStore({ id, deleting: true });
      } else {
        // eslint-disable-next-line
        console.error('Error deleting calendar event:', response);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting calendar event:', error);
    }

    closeEventPopup();
  };

  return (
    <div
      onClick={handleCalendarPageClick}
    >
      <div className="calendar-header">
        <div className="calendar-timezone">
          <CalendarTimeZoneSelect
            timeZone={timeZone}
            // Not allowing users to change timezone for now.
            // onTimeZoneChange={handleTimeZoneChange}
            dropdownButtonProps={{
              className: 'timezone-select__input--disabled',
            }}
            disabled
            name='Time Zone:'
          />
        </div>
        <div className="calendar-navigation">
          <div className="calendar-navigation__button__prev">
            <button
              type="button"
              className="calendar-navigation__button"
              onClick={() => nextPrevDate('prev')}
            >
              <i className="fa fa-angle-left" />
            </button>
          </div>
          <h3
            className="calendar-title"
            onClick={() => toggleDateTip()}
          >
            {currentMonth}
          </h3>
          <div className="calendar-navigation__button__next">
            <button
              type="button"
              className="calendar-navigation__button"
              onClick={() => nextPrevDate('next')}
            >
              <i className="fa fa-angle-right" />
            </button>
          </div>
          {
            displayDateTip &&
            calendarApi && (
              <DateNavigationForm
                selectedDate={selectedDate}
                projects={projects}
                calendarApi={calendarApi}
                toggleDateTip={toggleDateTip}
              />
            )
          }
        </div>
        <div className="calendar-view-options">
          <CalendarViewOptions
            options={calendarViewOptions}
            currentProjects={sortedProjects}
            calendarMicrofilters={calendarMicrofilters}
            handleProjectExclusionToggle={handleProjectExclusionToggle}
            handleAllProjectExclusions={handleAllProjectExclusions}
            handleMicrofilterChange={handleMicrofilterChange}
            handleFirstDayChange={handleFirstDayChange}
          />
        </div>
      </div>
      <div className="calendar-container">
        <div className="calendar-sidepanel__wrapper">
          <div className={clsx('calendar-sidepanel', sidepanelExpanded ? 'expanded' : 'collapsed')}>
            <CalendarSidePanel
              options={calendarViewOptions}
              currentProjects={projects}
              handleEventTypeToggle={handleEventTypeToggle}
              handleExternalCalendarToggle={handleExternalCalendarToggle}
              handleProjectExclusionToggle={handleProjectExclusionToggle}
              handleFirstDayChange={handleFirstDayChange}
              handleCalendarSyncSettingsToggle={handleCalendarSyncSettingsToggle}
              handleManageCalendar={handleManageCalendar}
              handleConnectCalendar={handleConnectCalendar}
              toggleSidePanel={toggleSidePanel}
            />
          </div>
          <ConnectModal />
        </div>
        <div
          id="calendar-wrapper"
          className="calendar-wrapper"
        >
          <FullCalendar
            events={filteredEvents}
            timeZone={timeZone}
            ref={calendarRef}
            firstDay={firstDayOfWeek}
            eventClick={eventClick}
            eventClassNames={getEventClassNames}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            eventContent={(arg) => renderEventContent(arg)}
            eventOrder={eventOrder(externalCalendarsOrder)}
            eventTimeFormat={getEventTimeFormat(timePreference)}
            dayMaxEvents={4}
            moreLinkContent={(arg) => `${arg.num} More`}
            dateClick={dateClick}
            datesSet={handleDateChange}
            eventDrop={handleEventDrop}
            dayPopoverFormat={getDayPopoverFormat()}
            eventAllow={() => isDragAndDropEnabled}
            headerToolbar={false}
            eventDidMount={handleEventMount}
            height="100%"
            {...restProps}
          />
        </div>
      </div>
      {!isCalendarSetupComplete && (
        <div className="calendar-loading-overlay">
          <Spinner />
        </div>
      )}
      {isOpen && (
        <>
          <CardContainer
            open={isOpen}
            reference={reference}
            calendarRef={calendarRef}
            showDeleteButton={false}
            handleCalendarEventDelete={() => handleCalendarEventDelete(eventDetails)}
            className={cardContainerClasses}
            eventDetails={eventDetails}
            closeEventPopup={closeEventPopup}
            hasUnsavedChanges={hasUnsavedChanges}
          >
            {renderCardContent()}
          </CardContainer>
        </>
      )}
      {manageCalendarOpen && (
        <APModal
          title="Manage Your Calendar"
          subtitle="Calendar Sync Settings"
          name="manage-calendar"
          open={manageCalendarOpen}
          onClose={handleManageCalendar}
          actions={(
            <button
              type="button"
              className="button button--secondary"
              onClick={handleManageCalendar}
            >
              Done
            </button>
          )}
        >
          <CalendarSyncSettings onCalendarSyncSettingsToggle={handleCalendarSyncSettingsToggle} />
        </APModal>
      )}
    </div>
  );
};

export default Calendar;
